import { zodResolver } from "@hookform/resolvers/zod";
import { RefreshCw } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useErrorDialog } from "@/shared/components/ErrorDialog";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import type { components } from "@/shared/lib/api.types";
import { useResetOnOpen } from "@/shared/lib/useResetOnOpen";
import { useCreateInvite } from "../api/invite.mutations";
import {
  type InviteFormInput,
  type InviteFormValues,
  inviteFormSchema,
} from "../roster.schemas";
import { CopyField } from "./CopyField";

type CreateInviteResponse = components["schemas"]["CreateInviteResponse"];

const DURATION_OPTIONS: {
  value: InviteFormValues["duration"];
  label: string;
}[] = [
  { value: "1d", label: "1 dag" },
  { value: "7d", label: "7 dagar" },
  { value: "30d", label: "30 dagar" },
];

export function GenerateInviteDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const form = useForm<InviteFormInput, unknown, InviteFormValues>({
    resolver: zodResolver(inviteFormSchema),
    defaultValues: { duration: "7d" },
  });
  const createInvite = useCreateInvite();
  const { showApiError } = useErrorDialog();

  const [result, setResult] = useState<CreateInviteResponse | null>(null);

  useResetOnOpen(open, () => {
    form.reset({ duration: "7d" });
    setResult(null);
  });

  const onSubmit = (values: InviteFormValues) =>
    createInvite.mutate(values, {
      onSuccess: (data) => setResult(data),
      onError: (e) => showApiError(e, { title: "Kunde inte skapa inbjudan" }),
    });

  const link = result ? `${window.location.origin}/join/${result.token}` : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Skapa inbjudan</DialogTitle>
          <DialogDescription>
            Skapa en länk som nya spelare använder för att skapa ett konto.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="duration"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Giltighetstid</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {DURATION_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              className="w-full"
              loading={createInvite.isPending}
            >
              <RefreshCw />
              Generera
            </Button>
          </form>
        </Form>

        {result && (
          <div className="border-t border-border pt-4">
            <CopyField
              key={link}
              label="Länk"
              value={link}
              copyLabel="Kopiera länk"
              copiedLabel="Kopierad"
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
